import { Badge, Card, Empty, Tag } from "antd";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@fitness-app/utils/src/styles/cn";

interface PricingFeature {
  name: string;
  value?: string;
  originalPrice?: string;
  isFree?: boolean;
  hideCheck?: boolean;
}

interface PricingPlan {
  title: string;
  best?: boolean;
  subtitle?: string;
  regularPrice: string;
  promoPrice: string;
  monthlyInfo: string;
  oneTimeInfo?: string;
  features: PricingFeature[];
  bonuses: PricingFeature[];
}

interface PricingPlanPreviewProps {
  plans: PricingPlan[];
}

function PlanFeature({ feature }: { feature: PricingFeature }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center">
        {!feature.hideCheck && <Check className="mr-2 h-4 w-4 flex-shrink-0 text-green-500" />}
        <span className={`text-sm ${feature.hideCheck ? "" : "ml-0"}`}>{feature.name}</span>
      </div>
      {feature.value && <p className="ml-6 text-sm font-semibold text-gray-600">{feature.value}</p>}
      {feature.isFree && (
        <div className="ml-6 space-y-1">
          <p className="text-xs text-gray-400 line-through">{feature.originalPrice}</p>
          <Badge color="green">GRATIS</Badge>
        </div>
      )}
    </div>
  );
}

const PricingPlanPreview = ({ plans }: PricingPlanPreviewProps) => {
  const { t } = useTranslation(["products", "common"]);

  if (!plans || plans.length === 0) {
    return (
      <Card>
        <Empty description={t<string>("pageConfig.noPricingPlansToPreview")} />
      </Card>
    );
  }

  //   const mostBonuses = plans.findIndex(
  //     (plan, _index, array) => plan.bonuses.length === Math.max(...array.map((p) => p.bonuses.length)),
  //   );

  const isOnlyOnePlan = plans.length === 1;

  return (
    <div className="w-full space-y-4 rounded-lg bg-gray-50 p-4">
      <h3 className="mb-4 text-center text-lg font-semibold">{t<string>("pageConfig.preview")}</h3>

      <div className={`mx-auto grid gap-4 ${isOnlyOnePlan ? "max-w-2xl grid-cols-1" : "grid-cols-2"}`}>
        {plans.map((plan, planIndex) => (
          <Card
            key={planIndex}
            className="relative overflow-hidden"
            title={
              <div className={cn("space-y-1")}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">{plan.title}</h3>
                  {plan.best && <Tag color="green">{t<string>("pageConfig.best")}</Tag>}
                </div>
                {plan.subtitle && <p className="text-sm font-normal text-gray-500">{plan.subtitle}</p>}
              </div>
            }
          >
            <div className="space-y-4">
              {/* Pricing */}
              <div className="space-y-2 border-b pb-4">
                <p className="text-sm text-gray-400 line-through">Cena Regularna {plan.regularPrice}</p>
                <p className="text-xl font-bold text-gray-900">Cena promocyjna {plan.promoPrice}</p>
                <p className="text-sm text-gray-500">{plan.monthlyInfo}</p>
                {plan.oneTimeInfo && <p className="text-sm font-semibold text-gray-700">{plan.oneTimeInfo}</p>}
              </div>

              {/* Features */}
              <div className="space-y-2">
                {plan.features.map((feature, featureIndex) => (
                  <PlanFeature key={featureIndex} feature={feature} />
                ))}
              </div>

              {/* Bonuses */}
              {plan.bonuses.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="font-semibold text-gray-900">BONUSY</h4>
                  {plan.bonuses.map((bonus, bonusIndex) => (
                    <PlanFeature key={bonusIndex} feature={bonus} />
                  ))}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PricingPlanPreview;
